// @ts-strict-ignore
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

import {apiClient} from 'common/util/api-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {GlobalAccessType} from 'common/widgets/create-entity-modal/types';

import {appApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';

interface CohortParams {
  name: string;
  description: string;
  selector?: Obj;
  behaviors?: Obj;
  groups?: Obj;
  // data_group_id must be a string because it can be bigger than Number.MAX_SAFE_INTEGER
  data_group_id?: string;
  global_access_type?: GlobalAccessType;
}

const CohortParamsKeys: Array<keyof CohortParams> = [
  `name`,
  `description`,
  `selector`,
  `behaviors`,
  `groups`,
  `data_group_id`,
  `global_access_type`,
];

class CohortsClient extends BaseClient {
  arrayFormatBracket = true;

  buildEndpoint({
    projectId,
    cohortID,
    workspaceId,
    isBulkDelete,
  }: {
    projectId?: number;
    cohortID?: number;
    workspaceId?: number;
    isBulkDelete?: boolean;
  }): string {
    let endpoint = appApiUrl(`cohorts/`, {projectId, workspaceId});
    if (isBulkDelete) {
      endpoint = endpoint + `bulk-delete/`;
    } else if (cohortID) {
      endpoint = endpoint + `${cohortID}/`;
    }
    return endpoint;
  }

  buildRequestBody(params) {
    if (params.cohort) {
      return JSON.stringify(params.cohort);
    }
    return JSON.stringify(params.queryParams);
  }

  buildQueryParams({dataGroupId, cohortIds}) {
    let queryParams = {};
    if (dataGroupId) {
      queryParams = {...queryParams, data_group_id: dataGroupId};
    }
    if (cohortIds) {
      queryParams = {...queryParams, id: cohortIds};
    }

    if (!isEmpty(queryParams)) {
      return queryParams;
    }
    return null;
  }

  processResponse(response) {
    return response.results;
  }

  getCohorts(
    {
      projectId,
      workspaceId,
      dataGroupId,
      cohortIds,
    }: {projectId?: number; workspaceId?: number; dataGroupId?: number; cohortIds?: number[]},
    queryOpts = {},
  ) {
    return this.query({projectId, workspaceId, dataGroupId, cohortIds}, queryOpts).then((results) => {
      return results.reduce((acc, serializedCohort) => {
        return {...acc, [serializedCohort.id]: Cohort.parseCohort(serializedCohort)};
      }, {});
    });
  }

  getCohortById({
    projectId,
    workspaceId,
    cohortID,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohortID: number;
  }): Promise<SerializedCohort> {
    return apiClient.get(this.buildEndpoint({projectId, workspaceId, cohortID}));
  }

  //TODO: Promise type
  //TODO [shayan]: need to adjust CohortParams
  createCohort({
    projectId,
    cohort,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohort: Partial<CohortParams>;
  }): Promise<SerializedCohort> {
    return apiClient.post(this.buildEndpoint({projectId, workspaceId}), pick(cohort, CohortParamsKeys));
  }

  deleteCohortById({
    projectId,
    cohortID,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohortID: number;
  }): Promise<{id: number}> {
    return apiClient.delete(this.buildEndpoint({projectId, cohortID, workspaceId}));
  }

  updateCohortById(
    {
      projectId,
      cohortID,
      workspaceId,
    }: {
      projectId?: number;
      workspaceId?: number;
      cohortID: number;
    },
    cohort: Partial<CohortParams>,
  ): Promise<SerializedCohort> {
    return apiClient.patch(this.buildEndpoint({projectId, cohortID, workspaceId}), pick(cohort, CohortParamsKeys));
  }

  bulkDeleteCohorts(projectId: number, cohortIds: any): Promise<any> {
    return apiClient.post(this.buildEndpoint({projectId, isBulkDelete: true}), {
      ...cohortIds,
    });
  }

  bulkUpdateCohorts(projectId: number, cohorts: CohortParams[]): Promise<any> {
    return apiClient.post(`${this.buildEndpoint({projectId})}bulk-update/`, {
      cohorts: cohorts.map((cohort) => pick(cohort, CohortParamsKeys)),
    });
  }

  restoreCohortById({
    projectId,
    cohortID,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohortID: number;
  }): Promise<{id: number}> {
    return apiClient.patch(this.buildEndpoint({projectId, cohortID, workspaceId}), {
      deleted: false,
    });
  }

  bulkRestoreCohorts(projectId: number, cohortIds: any): Promise<Cohort> {
    return apiClient.post(this.buildEndpoint({projectId, isBulkDelete: true}), {
      ...cohortIds,
      delete: false,
    });
  }

  verifyCohort({
    projectId,
    cohortID,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohortID: number;
  }): Promise<{cohort_id: number; verified: boolean}> {
    return apiClient.put(`${appApiUrl(`cohorts/`, {projectId, workspaceId})}${cohortID}/verify/`, {});
  }

  unverifyCohort({
    projectId,
    cohortID,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
    cohortID: number;
  }): Promise<{cohort_id: number; verified: boolean}> {
    return apiClient.delete(`${appApiUrl(`cohorts/`, {projectId, workspaceId})}${cohortID}/verify/`);
  }

  getVerifiedCohorts({
    projectId,
    workspaceId,
  }: {
    projectId?: number;
    workspaceId?: number;
  }): Promise<SerializedCohort[]> {
    return apiClient.get(appApiUrl(`cohorts/`, {projectId, workspaceId}), {verified: `true`});
  }

  bulkVerifyCohorts(
    projectId: number,
    cohortStates: Array<{cohort_id: number; updated_state: boolean}>,
    workspaceId?: number,
  ): Promise<{status: string; cohort_ids: number[]; skipped_cohort_ids: number[]}> {
    return apiClient.post(`${appApiUrl(`cohorts/`, {projectId, workspaceId})}verify/`, {
      cohort_states: cohortStates,
    });
  }
}

export const cohortsClient = new CohortsClient();
